import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form } from "react-bootstrap";
import { Pagination } from "react-bootstrap";
import { GoClock } from "react-icons/go";
import { CiHome } from "react-icons/ci";
import { RiDownloadLine } from "react-icons/ri";
import { FiFileText } from "react-icons/fi";
import SearchIcon from "../../../icons/SearchIcon";
import "./style.scss";
import { apiPageLimit, formatLocalDate } from "../../../constants/common";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { getStorageItem } from "../../../services/storage.service";
import {
  exportAuditTrail,
  getAuditTrailList,
} from "../../../core/data/redux/auditTrail/_auditTrailAction";
import { getPropertyListForFilter } from "../../../core/data/redux/property/_propertyAction";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  auditTrailActionOptions,
  auditTrailRoleOptions,
} from "../../../constants/utlis";
import LocationIcon from "../../../icons/LocationIcon";
import { CommonPagination } from "../../../core/components/common/CommnPagination";
import { formatDate, formatTime } from "../../../constants/date";

const CpaAuditTrail: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  const auditTrailData = useSelector((state: any) => state?.auditTrail);
  const auditTrail = auditTrailData?.auditTrailList;
  const totalCount = auditTrailData?.totalCount;

  const owner_uuid = getStorageItem("selectedClientUuid");

  const [page, setPage] = useState<number>(1);
  const pageSize = apiPageLimit;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false); // Loading state for export
  const [isPrinting, setIsPrinting] = useState(false); // Loading state for print
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null, // start date = null
    null, // end date = null
  ]);
  const [startDate, endDate] = dateRange;

  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch properties on component mount
  useEffect(() => {
    dispatch(getAuditTrailList({ owner_uuid }));
    getAllPropertyList();
  }, [dispatch]);

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({ owner_uuid }));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  };

  // Debounce search
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Fetch Audit Trail with debounce
  const fetchAuditTrail = useCallback(
    debounce(
      (
        search: string,
        selectedProperty: string,
        page: number,
        selectedRole: string,
        selectedAction: string,
        startDate: Date,
        endDate: Date | null
      ) => {
        const params: any = { page, limit: pageSize };

        if (search) params.search = search;
        if (selectedProperty) params.property = selectedProperty;
        if (selectedRole) params.role = selectedRole;
        if (selectedAction) params.action = selectedAction;
        if (startDate) params.start_date = formatLocalDate(startDate);
        if (endDate) params.end_date = formatLocalDate(endDate);
        if (owner_uuid) params.owner_uuid = owner_uuid;

        dispatch(getAuditTrailList(params));
      },
      500
    ),
    [dispatch]
  );

  // Run when searchTerm or selected changes
  useEffect(() => {
    fetchAuditTrail(
      searchTerm,
      selectedProperty,
      page,
      selectedRole,
      selectedAction,
      startDate,
      endDate
    );
  }, [
    searchTerm,
    selectedProperty,
    page,
    selectedRole,
    selectedAction,
    startDate,
    endDate,
    fetchAuditTrail,
  ]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle export
  const handleExportAuditTrail = async () => {
    setIsExporting(true);
    try {
      const response = await dispatch(exportAuditTrail({ owner_uuid }));

      const { success, message, file_url } = response.payload;

      if (success && file_url) {
        // Fetch the file as blob to ensure download
        const fileResponse = await fetch(file_url);
        if (!fileResponse.ok) {
          throw new Error('Failed to fetch file');
        }
        const blob = await fileResponse.blob();

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger the download
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file_url.split("/").pop() || "audit_trail.docx"; // Fallback filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Revoke the object URL after download
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

        // toast.success(message || "Audit trail exported successfully.");
      } else {
        toast.error("Failed to export audit trail: No file URL provided.");
      }
    } catch (error) {
      console.error("Error exporting audit trail:", error);
      toast.error("Failed to export audit trail. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrintSummary = async () => {
    setIsPrinting(true);
    try {
      const response = await dispatch(exportAuditTrail({ owner_uuid }));

      const { success, message, file_url } = response.payload;

      if (success && file_url) {
        // Fetch the file as blob so we can print it
        const fileResponse = await fetch(file_url);
        const blob = await fileResponse.blob();

        // Create a blob URL for printing
        const blobUrl = window.URL.createObjectURL(blob);

        // Open in a new tab
        const printWindow = window.open(blobUrl, "_blank");

        // Wait for file to load before printing
        printWindow?.addEventListener("load", () => {
          printWindow.print();
        });

        // Optionally revoke object URL after print
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } else {
        toast.error("Failed to fetch print summary file.");
      }
    } catch (error) {
      console.error("Error printing summary:", error);
      toast.error("Failed to print summary. Please try again.");
    } finally {
      setIsPrinting(false);
    }
  };

  const handleClearAllFilters = () => {
    setSelectedRole("");
    setSelectedProperty("");
    setSearchTerm("");
    setSelectedPropertyName("");
    setSelectedAction("");
    setDateRange([null, null]);
  }


  return (
    <div className="layout">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">

        <div className="profileSettings audit-trail-page-section ">
          <div className="rounded audit-trail-page">
            <div className="add-properties-heading d-flex justify-content-between align-items-center gap-3 mb-4">
              <h2 className="mb-0">Audit Trail </h2>

              <div className="button-area d-flex gap-2">
                <Button
                  className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                  variant="primary"
                  onClick={handleExportAuditTrail}
                  disabled={isExporting}
                >
                  <RiDownloadLine
                    style={{
                      fontSize: "20px",
                      color: "#fff",
                      marginRight: "8px",
                    }}
                  />
                  {isExporting ? "Exporting..." : "Export Audit Trail"}
                </Button>

                <Button
                  className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                  variant="primary"
                  onClick={handlePrintSummary}
                  disabled={isPrinting}
                >
                  <FiFileText
                    style={{
                      fontSize: "20px",
                      color: "#fff",
                      marginRight: "8px",
                    }}
                  />
                  {isPrinting ? "Preparing..." : "Print Summary"}
                </Button>
              </div>
            </div>

            <div className="row g-3 mb-3 row-boder-line mt-3">
              {/* Search */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 col-xxl-4">
                <div className="input-group mb-3 commonInput bg-light bg-white search-input-area">
                  <span className="input-group-text bg-white border-end-0 ">
                    <SearchIcon />
                  </span>

                  <input
                    type="text"
                    placeholder="Search action by name"
                    className="name form-control border-start-0 search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Date Range Picker */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-3 col-xxl-2">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update as [Date | null, Date | null]);
                  }}
                  isClearable={true}
                  dateFormat="yyyy-MM-dd"
                  className="form-control commonInput"
                  placeholderText="Select date range"
                />
              </div>

              {/* Role select */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-2">
                <select
                  className="form-select form-control commonInput"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="" disabled hidden>
                    Choose Role
                  </option>
                  {auditTrailRoleOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Property dropdown */}
              <div
                className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-2"
                ref={dropdownRef}
              >
                <div className="dropdown log-hour-list-page-dropdown">
                  <button
                    className="form-select d-flex justify-content-between align-items-center form-control commonInput"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {selectedPropertyName || "Choose Property "}
                    <i
                      className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div
                      className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                      style={{ maxHeight: "initial" }}
                    >
                      <div className="input-group mb-3 commonInput bg-light bg-white">
                        <span className="input-group-text bg-white border-end-0 ">
                          <SearchIcon />
                        </span>
                        <input
                          type="text"
                          placeholder="Search properties by name or address"
                          className="name form-control border-start-0"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>

                      {filteredProperties.length > 0 ? (
                        filteredProperties.map((property, index) => (
                          <div
                            key={property?.uuid || index}
                            className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput bg-light"
                            onClick={() => {
                              setSelectedProperty(property?.uuid);
                              setSelectedPropertyName(property?.name);
                              setIsDropdownOpen(false);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <label className="form-check-label ms-2">
                              <div className="label-inner-content d-flex flex-column gap-2">
                                <strong>{property.name}</strong>
                                <small className="text-muted d-flex align-items-center">
                                  <LocationIcon />
                                  {property.address}, {property?.city},{" "}
                                  {property?.state} {property?.zipcode}
                                </small>
                              </div>
                            </label>
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="propertyRadio"
                              checked={selectedPropertyName === property.name}
                              readOnly
                            />
                          </div>
                        ))
                      ) : (
                        <div className="text-muted text-center">
                          No results found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Status select */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-2">
                <select
                  className="form-select form-control commonInput"
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                >
                  <option value="" disabled hidden>
                    Select Action
                  </option>
                  {auditTrailActionOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 col-xxl-1 mb-2">
                <div className="clear-all-button">
                  <button onClick={() => handleClearAllFilters()}>Clear All</button>
                </div>
              </div>
            </div>

            {auditTrail?.length > 0 ? (
              <>
                <div className="row g-3 mt-3 mb-3 pb-3 pt-3">
                  <div className="grid-container mt-0">
                    {auditTrail?.map((audit: any) => {
                      return (
                        <div className="box h-100">
                          <div className="custom-card mx-auto h-100">
                            <div className="card-body">
                              <div className="d-flex gap-2 mt-2 justify-content-between flex-wrap">
                                <span
                                  className={
                                    audit?.user_role?.toLowerCase() == "spouse"
                                      ? "category-badge tag-red red"
                                      : "category-badge tag-sky-purple"
                                  }
                                >
                                  {audit?.user_role}
                                </span>
                                <span className="category-badge black-bg">
                                  {formatDate(audit?.date)}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center flex-wrap mt-3">
                                <h5 className="mb-0 fw-bold ">
                                  {audit?.action}
                                </h5>
                              </div>
                              <div className="visit-text mt-2 mb-0 text-left">
                                {audit?.log_hou && (
                                  <p className="text-grey text-left  d-flex align-items-center">
                                    <GoClock className="me-2" />
                                    {formatTime(audit?.log_hour)}
                                  </p>
                                )}

                                <p className="text-grey text-left ">
                                  <CiHome className="me-2" />
                                  {audit?.property?.address},{" "}
                                  {audit?.property?.city},{" "}
                                  {audit?.property?.state}
                                </p>

                                <p className=" text-left text-black">
                                  <strong className="text-black">
                                    Details:{" "}
                                  </strong>
                                  {audit?.details}
                                </p>
                                <p className="text-grey text-left d-flex align-items-center gap-3">
                                  <span>
                                    <strong>IP</strong>: {audit?.ip_address}
                                  </span>
                                  <span>
                                    <strong>Device:</strong>{" "}
                                    {audit?.device_type}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Pagination */}
                <CommonPagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </>
            ) : (
              <div className="visit-text text-center m-5">
                <strong> No results found. </strong>
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
};

export default CpaAuditTrail;
